import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { getLoginLogs, getLoginLogsStats, getActivityLogs, getActivityLogsStats } from '../api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LogIn, Shield, AlertTriangle, CheckCircle, XCircle, 
  RefreshCw, Search, Clock, UserCheck, UserX, Activity,
  FileEdit, Trash2, Eye, Download, Plus, Users, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

const LoginAuditLog = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('login');
  
  // Login logs state
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSuccess, setFilterSuccess] = useState('all');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  // Activity logs state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [filterActivityFromDate, setFilterActivityFromDate] = useState('');
  const [filterActivityToDate, setFilterActivityToDate] = useState('');

  const fetchLoginLogs = async () => {
    try {
      setLoading(true);
      const params = { limit: 200 };
      
      if (filterSuccess !== 'all') {
        params.success = filterSuccess === 'true';
      }
      if (filterEmail) {
        params.email = filterEmail;
      }
      if (filterFromDate) {
        params.from_date = filterFromDate;
      }
      if (filterToDate) {
        params.to_date = filterToDate;
      }
      
      const [logsRes, statsRes] = await Promise.all([
        getLoginLogs(params),
        getLoginLogsStats()
      ]);
      
      setLogs(logsRes.data.logs || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load login logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setActivityLoading(true);
      const params = { limit: 200 };
      
      if (filterAction !== 'all') {
        params.action = filterAction;
      }
      if (filterModule !== 'all') {
        params.module = filterModule;
      }
      if (filterActivityFromDate) {
        params.from_date = filterActivityFromDate;
      }
      if (filterActivityToDate) {
        params.to_date = filterActivityToDate;
      }
      
      const [logsRes, statsRes] = await Promise.all([
        getActivityLogs(params),
        getActivityLogsStats()
      ]);
      
      setActivityLogs(logsRes.data.logs || []);
      setActivityStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load activity logs');
      console.error(error);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'login') {
      fetchLoginLogs();
    } else {
      fetchActivityLogs();
    }
  }, [activeTab]);

  const handleLoginSearch = () => {
    fetchLoginLogs();
  };

  const handleActivitySearch = () => {
    fetchActivityLogs();
  };

  const clearLoginFilters = () => {
    setFilterSuccess('all');
    setFilterEmail('');
    setFilterFromDate('');
    setFilterToDate('');
    setTimeout(fetchLoginLogs, 100);
  };

  const clearActivityFilters = () => {
    setFilterAction('all');
    setFilterModule('all');
    setFilterActivityFromDate('');
    setFilterActivityToDate('');
    setTimeout(fetchActivityLogs, 100);
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      admin: { bg: 'bg-purple-100', text: 'text-purple-700' },
      hr_manager: { bg: 'bg-blue-100', text: 'text-blue-700' },
      hr_executive: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
      accounts_manager: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
      employee: { bg: 'bg-slate-100', text: 'text-slate-700' }
    };
    const style = roleStyles[role] || { bg: 'bg-slate-100', text: 'text-slate-700' };
    return <Badge className={`${style.bg} ${style.text} capitalize`}>{role?.replace('_', ' ')}</Badge>;
  };

  const getActionIcon = (action) => {
    const icons = {
      CREATE: <Plus className="h-4 w-4 text-green-600" />,
      UPDATE: <FileEdit className="h-4 w-4 text-blue-600" />,
      DELETE: <Trash2 className="h-4 w-4 text-red-600" />,
      VIEW: <Eye className="h-4 w-4 text-slate-600" />,
      EXPORT: <Download className="h-4 w-4 text-purple-600" />,
      APPROVE: <CheckCircle className="h-4 w-4 text-green-600" />,
      REJECT: <XCircle className="h-4 w-4 text-red-600" />,
      LOGIN: <LogIn className="h-4 w-4 text-indigo-600" />,
    };
    return icons[action] || <Activity className="h-4 w-4 text-slate-600" />;
  };

  const getActionBadge = (action) => {
    const styles = {
      CREATE: 'bg-green-100 text-green-700',
      UPDATE: 'bg-blue-100 text-blue-700',
      DELETE: 'bg-red-100 text-red-700',
      VIEW: 'bg-slate-100 text-slate-700',
      EXPORT: 'bg-purple-100 text-purple-700',
      APPROVE: 'bg-emerald-100 text-emerald-700',
      REJECT: 'bg-rose-100 text-rose-700',
      LOGIN: 'bg-indigo-100 text-indigo-700',
    };
    return (
      <Badge className={`${styles[action] || 'bg-slate-100 text-slate-700'} gap-1`}>
        {getActionIcon(action)}
        {action}
      </Badge>
    );
  };

  const getModuleIcon = (module) => {
    const icons = {
      employees: <Users className="h-4 w-4" />,
      leaves: <Clock className="h-4 w-4" />,
      attendance: <Activity className="h-4 w-4" />,
      documents: <FolderOpen className="h-4 w-4" />,
    };
    return icons[module] || <Activity className="h-4 w-4" />;
  };

  // Check access via Permissions module
  const hasAccess = useSectionAccess('/login-audit-log');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-96">
          <Shield className="h-16 w-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">You don't have permission to view audit logs.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="login-audit-log">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-500 mt-1">Monitor all login and user activities</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="login" className="flex items-center gap-2">
              <LogIn className="h-4 w-4" />
              Login Audit Log
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              User Activity Log
            </TabsTrigger>
          </TabsList>

          {/* Login Audit Tab */}
          <TabsContent value="login" className="space-y-6 mt-6">
            {/* Login Stats Cards */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <LogIn className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Logins</p>
                        <p className="text-2xl font-bold text-slate-900">{stats.total_logins}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <UserCheck className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Successful</p>
                        <p className="text-2xl font-bold text-green-600">{stats.successful_logins}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <UserX className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Failed</p>
                        <p className="text-2xl font-bold text-red-600">{stats.failed_logins}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Clock className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Last 24 Hours</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {stats.last_24_hours?.total || 0}
                          {stats.last_24_hours?.failed > 0 && (
                            <span className="text-sm text-red-500 ml-1">
                              ({stats.last_24_hours.failed} failed)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Login Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-48">
                    <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                    <Select value={filterSuccess} onValueChange={setFilterSuccess}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="true">Successful</SelectItem>
                        <SelectItem value="false">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-64">
                    <Label className="text-xs text-slate-500 mb-1 block">Email</Label>
                    <Input
                      placeholder="Search by email..."
                      value={filterEmail}
                      onChange={(e) => setFilterEmail(e.target.value)}
                    />
                  </div>
                  
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">From Date</Label>
                    <Input
                      type="date"
                      value={filterFromDate}
                      onChange={(e) => setFilterFromDate(e.target.value)}
                    />
                  </div>
                  
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">To Date</Label>
                    <Input
                      type="date"
                      value={filterToDate}
                      onChange={(e) => setFilterToDate(e.target.value)}
                    />
                  </div>
                  
                  <Button onClick={handleLoginSearch} className="bg-indigo-600 hover:bg-indigo-700">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  
                  <Button variant="outline" onClick={clearLoginFilters}>
                    Clear
                  </Button>
                  
                  <Button onClick={fetchLoginLogs} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Login Logs Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <LogIn className="h-5 w-5 text-indigo-600" />
                  Login History ({logs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <LogIn className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>No login records found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">User</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Role</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Timestamp</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, index) => (
                          <tr key={log.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              {log.success ? (
                                <Badge className="bg-green-100 text-green-700 gap-1">
                                  <CheckCircle size={12} />
                                  Success
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 gap-1">
                                  <XCircle size={12} />
                                  Failed
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-medium text-slate-900">
                                {log.user_name || '-'}
                              </p>
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-slate-600">{log.email}</p>
                            </td>
                            <td className="py-3 px-4">
                              {log.user_role ? getRoleBadge(log.user_role) : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm text-slate-600">
                                {new Date(log.timestamp).toLocaleString()}
                              </p>
                            </td>
                            <td className="py-3 px-4">
                              {log.failure_reason && (
                                <span className="text-xs text-red-600 flex items-center gap-1">
                                  <AlertTriangle size={12} />
                                  {log.failure_reason}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Activity Tab */}
          <TabsContent value="activity" className="space-y-6 mt-6">
            {/* Activity Stats Cards */}
            {activityStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Activity className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Activities</p>
                        <p className="text-2xl font-bold text-slate-900">{activityStats.total_activities}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <Plus className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Create Actions</p>
                        <p className="text-2xl font-bold text-green-600">
                          {activityStats.activities_by_action?.CREATE || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileEdit className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Update Actions</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {activityStats.activities_by_action?.UPDATE || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Clock className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Today's Activities</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {activityStats.today || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Most Active Users */}
            {activityStats?.most_active_users?.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    Most Active Users
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {activityStats.most_active_users.slice(0, 5).map((u, idx) => (
                      <Badge key={idx} variant="outline" className="px-3 py-1">
                        {u.user_name} ({u.count} actions)
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Activity Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-48">
                    <Label className="text-xs text-slate-500 mb-1 block">Action Type</Label>
                    <Select value={filterAction} onValueChange={setFilterAction}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Actions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Actions</SelectItem>
                        <SelectItem value="LOGIN">Login</SelectItem>
                        <SelectItem value="CREATE">Create</SelectItem>
                        <SelectItem value="UPDATE">Update</SelectItem>
                        <SelectItem value="DELETE">Delete</SelectItem>
                        <SelectItem value="VIEW">View</SelectItem>
                        <SelectItem value="EXPORT">Export</SelectItem>
                        <SelectItem value="APPROVE">Approve</SelectItem>
                        <SelectItem value="REJECT">Reject</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-48">
                    <Label className="text-xs text-slate-500 mb-1 block">Module</Label>
                    <Select value={filterModule} onValueChange={setFilterModule}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Modules" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Modules</SelectItem>
                        <SelectItem value="auth">Authentication</SelectItem>
                        <SelectItem value="employees">Employees</SelectItem>
                        <SelectItem value="leaves">Leaves</SelectItem>
                        <SelectItem value="attendance">Attendance</SelectItem>
                        <SelectItem value="payroll">Payroll</SelectItem>
                        <SelectItem value="documents">Documents</SelectItem>
                        <SelectItem value="settings">Settings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">From Date</Label>
                    <Input
                      type="date"
                      value={filterActivityFromDate}
                      onChange={(e) => setFilterActivityFromDate(e.target.value)}
                    />
                  </div>
                  
                  <div className="w-40">
                    <Label className="text-xs text-slate-500 mb-1 block">To Date</Label>
                    <Input
                      type="date"
                      value={filterActivityToDate}
                      onChange={(e) => setFilterActivityToDate(e.target.value)}
                    />
                  </div>
                  
                  <Button onClick={handleActivitySearch} className="bg-indigo-600 hover:bg-indigo-700">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  
                  <Button variant="outline" onClick={clearActivityFilters}>
                    Clear
                  </Button>
                  
                  <Button onClick={fetchActivityLogs} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Activity Logs Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5 text-indigo-600" />
                  Activity History ({activityLogs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {activityLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : activityLogs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Activity className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>No activity records found</p>
                    <p className="text-sm text-slate-400 mt-1">User activities will appear here as they are logged</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Action</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">User</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Module</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Description</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityLogs.map((log, index) => (
                          <tr key={log.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              {getActionBadge(log.action)}
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-900">{log.user_name}</p>
                                {log.user_role && (
                                  <span className="text-xs text-slate-500 capitalize">
                                    {log.user_role.replace('_', ' ')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {getModuleIcon(log.module)}
                                <span className="capitalize text-slate-700">{log.module}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-slate-600 text-sm">{log.description}</p>
                              {log.entity_id && (
                                <span className="text-xs text-slate-400">ID: {log.entity_id}</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm text-slate-600">
                                {new Date(log.timestamp).toLocaleString()}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default LoginAuditLog;
